<template>
  <div
    v-if="hasBanners"
    :class="classes"
  >
    <client-only>
      <swiper
        v-if="isSliderMode"
        v-slot="{ item }"
        :items="banners"
        dots
        :slides-to-show="2"
      >
        <banner
          :data="item"
          :slot-name="slotName"
          class="banners-slot__banner-slide"
        />
      </swiper>
      <div v-else class="banners-slot__wrap">
        <banner
          v-for="bannerItem in banners"
          :key="bannerItem.id"
          :data="bannerItem"
          :slot-name="slotName"
          class="banners-slot__banner"
        />
      </div>
    </client-only>
  </div>
</template>

<script lang="ts">
import { useHead } from '@vueuse/head';
import {
  computed, defineComponent, inject, onServerPrefetch,
} from 'vue';

import Swiper from '@kit/swiper/swiper.vue';

import Banner from '@/components/banner/banner.vue';
import { useBanner } from '@/composables/useBanner';
import { IAdaptive } from '@/utils/system/adaptive';

export default defineComponent({
  name: 'banners-slot',

  components: { Banner, Swiper },

  props: {
    slotName: {
      type: String,
      required: true,
    },
    withCategory: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const adaptive = inject<IAdaptive>('adaptive');

    const banner = useBanner();

    const ogImage = computed(() => {
      const desktop = banner.getFirstBanner(props.slotName)?.content?.desktop;

      if (typeof desktop === 'object') {
        return desktop.body?.image;
      }
      return '';
    });

    onServerPrefetch(async () => {
      await banner.loadBanners({
        slots: [props.slotName],
        withCategory: props.withCategory,
      });

      const meta = [];

      if (ogImage.value) {
        meta.push({
          property: 'og:image',
          content: ogImage,
        });
      }

      useHead(
        {
          meta,
        },
        {
          mode: 'server',
        },
      );
    });

    return {
      adaptive,
      banner,
    };
  },

  computed: {
    banners() {
      return this.banner.getBanner(this.slotName);
    },
    bannersCounter() {
      return this.banners.length;
    },
    hasBanners() {
      return !!this.bannersCounter;
    },
    isSliderMode() {
      return this.banners?.length > 2 && this.adaptive?.isMobile;
    },
    classes() {
      const base = 'banners-slot';

      return [
        base,
        { [`${base}_mode_slider`]: this.isSliderMode },
      ];
    },
  },
});
</script>

<style lang="scss">
.banners-slot {
  display: flex;
  flex-direction: column;

  &_mode_slider {
    flex-direction: row;
  }

  @include breakpoint("tablet") {
    flex-direction: row;
  }

  &__wrap {
    display: flex;
    flex-direction: column;

    @include breakpoint("tablet") {
      flex-direction: row;
    }
  }

  .banner {
    flex: 0 1 auto;
    margin-bottom: $mobileOffset;

    @include breakpoint("tablet") {
      margin-right: $mobileOffset;
      margin-bottom: 0;

      &:last-child {
        margin-right: 0;
      }
    }

    @include breakpoint("desktop") {
      margin-right: $mobileOffsetDesktop;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
